/**
 * List state encoded in the URL.
 *
 * The URL is the source of truth rather than component state, so a filtered
 * view can be linked, bookmarked and reloaded — PRD §7 expects people to share
 * "the stalled pilots in Iberia" with a colleague, which is impossible if the
 * filters live only in memory.
 *
 * The pure functions here are separated from the hook so the parsing rules can
 * be tested directly: a sort parameter that silently falls back to the default
 * is a bug nobody notices until a column stops sorting.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.15
 */

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  field: string;
  direction: SortDirection;
}

export interface TableState {
  page: number;
  pageSize: number;
  sort: SortState | null;
  /** Everything else in the query string, so filters ride along untouched. */
  filters: Record<string, string>;
}

/** Parameters this module owns; everything else is treated as a filter. */
const RESERVED = new Set(['page', 'pageSize', 'sort']);

export const DEFAULT_PAGE_SIZE = 50;

/** Page sizes offered in the UI. */
export const PAGE_SIZES = [25, 50, 100] as const;

/**
 * Reads a positive integer.
 *
 * A malformed value falls back to the default rather than propagating `NaN`
 * into a query the API would then reject — a hand-edited URL should degrade,
 * not error.
 */
function readPositiveInt(value: string | null, fallback: number): number {
  if (value === null) return fallback;

  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Parses the sort parameter, `field:direction`.
 *
 * `allowedFields` is required: without it a crafted URL could sort by any
 * column name, and the API's own allow-list would reject the request — a 400
 * where the user expected a table.
 */
export function parseSort(
  value: string | null,
  allowedFields: readonly string[],
): SortState | null {
  if (value === null || value === '') return null;

  const [field = '', rawDirection = 'asc'] = value.split(':');
  if (!allowedFields.includes(field)) return null;

  return {
    field,
    direction: rawDirection === 'desc' ? 'desc' : 'asc',
  };
}

/** Serialises a sort state back into the parameter form. */
export function serialiseSort(sort: SortState | null): string | null {
  return sort === null ? null : `${sort.field}:${sort.direction}`;
}

/** Reads the full table state out of a query string. */
export function parseTableState(
  params: URLSearchParams,
  allowedSortFields: readonly string[],
): TableState {
  const filters: Record<string, string> = {};

  for (const [key, value] of params.entries()) {
    if (!RESERVED.has(key) && value !== '') {
      filters[key] = value;
    }
  }

  return {
    page: readPositiveInt(params.get('page'), 1),
    pageSize: readPositiveInt(params.get('pageSize'), DEFAULT_PAGE_SIZE),
    sort: parseSort(params.get('sort'), allowedSortFields),
    filters,
  };
}

/**
 * Builds the next query string.
 *
 * Defaults are omitted so a pristine list has a clean URL — `?page=1&pageSize=50`
 * on first load is noise in a shared link. Empty filters are dropped for the
 * same reason.
 */
export function buildSearchParams(state: TableState): URLSearchParams {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(state.filters)) {
    if (value !== '') params.set(key, value);
  }

  if (state.page > 1) params.set('page', String(state.page));
  if (state.pageSize !== DEFAULT_PAGE_SIZE)
    params.set('pageSize', String(state.pageSize));

  const sort = serialiseSort(state.sort);
  if (sort !== null) params.set('sort', sort);

  return params;
}

/**
 * The sort state after clicking a column header.
 *
 * Ascending first, then descending, then unsorted. The third click matters:
 * without it there is no way back to the server's default ordering once a
 * column has been touched.
 */
export function nextSort(current: SortState | null, field: string): SortState | null {
  if (current?.field !== field) {
    return { field, direction: 'asc' };
  }

  return current.direction === 'asc' ? { field, direction: 'desc' } : null;
}
